import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  ServiceWhereInput,
  ServiceReminderTimingOffsetUnit,
  ServiceReminderTimingType,
  ReminderSettingsServiceFragment,
  ReminderSettingsServiceTimingFragment,
  useUpdateReminderSettingsServiceMutation,
  useUpdateServicesTimingsMutation,
  ServiceUpdateManyMutationInput,
  useToggleServicesTimingsDeliveryOptionsMutation,
  useGetClinicPhoneNumbersQuery,
  ServiceCapability,
  PhoneNumberStatus,
} from 'shared/types/graphql';
import ReminderTimingRow from './ReminderTimingRow';
import { Box, Flex, VStack, Collapse, List, ListItem } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Checkbox } from '@televet/kibble-ui/build/components/Checkbox';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import {
  Modal,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooterButtons,
} from '@televet/kibble-ui/build/components/Modal';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import { InfoIcon } from 'shared/components/InfoIcon';
import { ServiceReminderTimingInput } from 'shared/types/graphql';
import { formatPluralization } from 'shared/utils';
import { comparator } from '../utils/timingComparator';
import ScheduleServiceReminderActivation from './ScheduleServiceReminderActivation';
import { isPast, subDays } from 'date-fns';
import useClinicUser from 'shared/hooks/useClinicUser';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { TextMessagePopover } from './TextMessagePopover';
import InfoPopover from 'shared/components/InfoPopover';
import { IntegrationSystemDisplayNameType } from 'shared/providers/IntegrationsProvider';
import HasFeature from 'shared/components/HasFeature';
import { FeatureFlagName } from 'shared/enums';

interface props {
  whereServices: ServiceWhereInput;
  servicesCount: number;
  selectedService?: ReminderSettingsServiceFragment;
  selectedServices?: ReminderSettingsServiceFragment[];
  isOpen: boolean;
  showPostcards: boolean;
  onSave: () => void;
  onClose: () => void;
  integrationDataProvider: IntegrationSystemDisplayNameType | null;
  showBillingCode: boolean;
}

export interface ITime {
  hours: number;
  minutes: number;
}
export enum IPopoverContentType {
  Text_Messaging_unavailable = 'Text_Messaging_Unavailable',
  Delivery_Option_Unavailable = 'Delivery_Option_Unavailable',
}

const EditRemindersModal = ({
  whereServices,
  servicesCount,
  selectedService,
  selectedServices,
  isOpen,
  showPostcards,
  onSave,
  onClose,
  integrationDataProvider,
  showBillingCode,
}: props): JSX.Element => {
  const { toastMessagePop } = useCustomToast();
  const { currentClinicId } = useClinicUser();

  const serviceData = selectedService || (selectedServices?.length === 1 ? selectedServices[0] : undefined);

  // Trigger confirmation modal if editing more than on record (Auto-confirm if editing one)
  const [confirming, setConfirming] = useState(false);
  const [timings, setTimings] = useState<ReminderSettingsServiceTimingFragment[]>(serviceData?.reminderTimings || []);
  const [displayName, setDisplayName] = useState(serviceData?.name);
  const [showNoRemindersError, setShowNoRemindersError] = useState(false);
  const [showMissingDeliveryOptionErr, setShowMissingDeliveryOptionErr] = useState(false);
  const [showMatchingRemindersError, setShowMatchingRemindersError] = useState(false);
  const [showFalsyOffsetError, setShowFalsyOffsetError] = useState(false);
  const [showPastDateError, setShowPastDateError] = useState(false);
  const [isEmail, setIsEmail] = useState(serviceData?.reminderTimings[0]?.isEmail);
  const [isPetPortal, setIsPetPortal] = useState(serviceData?.isPetPortal);
  const [isSms, setIsSms] = useState(serviceData?.reminderTimings[0]?.isSms);
  const [isPush, setIsPush] = useState(serviceData?.reminderTimings[0]?.isPush);
  const [isPostcard, setIsPostcard] = useState(serviceData?.isPostcard);
  const [reminderActivationDate, setReminderActivationDate] = useState<Date | null>(
    serviceData?.reminderActivationDate ? new Date(serviceData.reminderActivationDate) : null,
  );
  const [active, setActive] = useState(!!reminderActivationDate ? isPast(reminderActivationDate) : false);
  const [bulkServiceActiveState, setBulkServiceActiveState] = useState<boolean | null>(null);
  const indetermindateState = useMemo(
    () => servicesCount > 1 && bulkServiceActiveState === null,
    [bulkServiceActiveState, servicesCount],
  );

  const isDeliveryOptionDisabled = useMemo(
    () => timings.length === 0 && !active && servicesCount === 1,
    [active, servicesCount, timings.length],
  );

  const [updateTimings, { loading }] = useUpdateServicesTimingsMutation({
    refetchQueries: ['getReminderSettingsClinicServices', 'getReminderSettingsClinicServicesTimings'],
    awaitRefetchQueries: true,
  });

  const { data: phoneNumbersData, loading: isClinicPhoneNumbersLoading } = useGetClinicPhoneNumbersQuery({
    variables: {
      where: {
        clinicId: { equals: currentClinicId },
        isDeleted: { equals: false },
        isActive: { equals: true },
        OR: [
          { status: { equals: PhoneNumberStatus.Hosted } },
          { status: { equals: PhoneNumberStatus.PurchasedFromBandwidth } },
          { status: { equals: PhoneNumberStatus.PurchasedFromTwilio } },
        ],
        capability: { equals: ServiceCapability.Sms },
      },
    },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const clinicPhoneNumbers = phoneNumbersData?.findManyClinicPhoneNumber;

  const servicesData = useMemo(() => {
    let activationDate = reminderActivationDate;
    if (active) {
      activationDate = subDays(Date.now(), 1);
    }

    const data: ServiceUpdateManyMutationInput = indetermindateState
      ? {}
      : {
          reminderActivationDate: activationDate,
        };

    /**
     * We only toggle `isReminderActive` to false here because the queue job is
     * responsible for flipping it to true when reminders are first queued for
     * this service just before it's scheduled to go active.
     */
    if (!active && !indetermindateState) {
      data.isReminderActive = false;
    }

    if (isPetPortal !== undefined) data.isPetPortal = isPetPortal;
    if (isPostcard !== undefined) data.isPostcard = isPostcard;

    return servicesCount > 1 ? data : { name: displayName, ...data };
  }, [reminderActivationDate, active, indetermindateState, isPetPortal, isPostcard, servicesCount, displayName]);

  const [updateServices] = useUpdateReminderSettingsServiceMutation({
    variables: {
      where: whereServices,
      data: servicesData,
    },
    refetchQueries: ['getReminderSettingsClinicServices', 'getReminderSettingsClinicServicesTimings'],
  });

  const [updateTimingsDeliveryOptions] = useToggleServicesTimingsDeliveryOptionsMutation({
    variables: {
      where: whereServices,
      data: {
        isEmail,
        isSms,
        isPush,
      },
    },
    refetchQueries: ['getReminderSettingsClinicServices', 'getReminderSettingsClinicServicesTimings'],
    awaitRefetchQueries: true,
  });

  const getServiceReminderTimingInputData = (
    timings: ReminderSettingsServiceTimingFragment[],
  ): ServiceReminderTimingInput[] => {
    return timings.map(({ timingType, offset, offsetUnit, hourOfDay, minuteOfHour }) => {
      const data: ServiceReminderTimingInput = { timingType, offset, offsetUnit, hourOfDay, minuteOfHour };
      if (isEmail !== undefined) data.isEmail = isEmail;
      if (isSms !== undefined) data.isSms = isSms;
      if (isPush !== undefined) data.isPush = isPush;
      return data;
    });
  };

  const hasDuplicates = (arr: ReminderSettingsServiceTimingFragment[]): boolean => {
    // eslint-disable-next-line
    const clean = arr.map(({ id, __typename, ...item }) => item);
    const set = new Set(clean.map((i) => JSON.stringify(i)));
    return set.size < arr.length;
  };

  const handleSave = async (): Promise<void> => {
    // TODO: refactor with generic validation, with react-hook-form
    setShowNoRemindersError(false);
    setShowMatchingRemindersError(false);
    setShowFalsyOffsetError(false);
    setShowMissingDeliveryOptionErr(false);

    let hasSomeError = false;
    if ((active || !!reminderActivationDate) && !isSms && !isPetPortal && !isEmail && !isPostcard && !isPush) {
      setShowMissingDeliveryOptionErr(true);
      hasSomeError = true;
    }

    if (timings.length === 0 && (active || !!reminderActivationDate)) {
      setShowNoRemindersError(true);
      hasSomeError = true;
    }

    if (hasDuplicates(timings)) {
      setShowMatchingRemindersError(true);
      hasSomeError = true;
    }

    if (timings.some((timing) => timing.offset < 1)) {
      setShowFalsyOffsetError(true);
      hasSomeError = true;
    }

    if (!active && !!reminderActivationDate && isPast(reminderActivationDate)) return;

    if (hasSomeError) {
      return;
    }

    // If editing more than one record, have the user confirm changes.
    if (servicesCount > 1 && !confirming) {
      setConfirming(true);
      return;
    }

    const where =
      servicesCount > 1
        ? whereServices
        : {
            id: {
              in: [serviceData?.id || ''],
            },
          };

    try {
      if (timings.length) {
        await updateTimings({
          variables: {
            where,
            data: getServiceReminderTimingInputData(timings),
          },
        });
      } else {
        if (servicesCount > 1) {
          if (isEmail !== undefined || isSms !== undefined || isPush !== undefined) {
            await updateTimingsDeliveryOptions({
              variables: {
                where,
                data: {
                  isEmail,
                  isSms,
                  isPush,
                },
              },
            });
          }
        }
      }

      const { data } = await updateServices();
      toastMessagePop(
        `${data?.updateManyService?.count} ${formatPluralization({
          word: 'service reminder',
          singularCriteria: data?.updateManyService?.count,
        })} successfully updated.`,
      );

      onSave();
    } catch (e) {
      Sentry.captureException(e);
      toastMessagePop(
        'Whoops! An error occurred while updating the reminder(s). Please try again.',
        STATUSES.error,
        5000,
      );
      console.error(e);
    }
  };

  const handleAddTimingClick = (): void => {
    const timing = {
      id: '',
      timingType: ServiceReminderTimingType.BeforeDue,
      offset: 1,
      offsetUnit: ServiceReminderTimingOffsetUnit.Days,
      hourOfDay: 8,
      minuteOfHour: 0,
      isEmail: !!isEmail,
      isSms: !!isSms,
      isPush: !!isPush,
    };
    setTimings([...timings, timing]);
    setShowNoRemindersError(false);
  };

  const handleTimingEdit = (index: number, name: string, value: string | number, time?: ITime): void => {
    const timingsCopy = [...timings];
    let editedTiming;
    if (name === 'time' && time) {
      editedTiming = {
        ...timingsCopy[index],
        hourOfDay: time.hours,
        minuteOfHour: time.minutes,
      };
    } else {
      editedTiming = {
        ...timingsCopy[index],
        [name]: value,
      };
    }
    timingsCopy[index] = editedTiming;
    setTimings(timingsCopy);
  };

  const handleRemoveTiming = (index: number): void => {
    setTimings(timings.filter((_, tIndex) => tIndex !== index));
  };

  const handleDisplayNameChange = (e: ChangeEvent<HTMLInputElement>): void => setDisplayName(e.target.value);
  const handleActiveChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (servicesCount > 1) {
      setBulkServiceActiveState(e.target.checked);
    }
    setActive(e.target.checked);
    setReminderActivationDate(null);
  };

  useEffect(() => {
    setTimings((timings) => [...timings].sort(comparator));
  }, []);

  const handleDateChange = (date: Date): void => {
    if (!active && isPast(date)) {
      setShowPastDateError(true);
      return;
    }
    setShowPastDateError(false);
    setReminderActivationDate(date);
  };

  const handleRemoveDate = (): void => {
    setReminderActivationDate(null);
    setShowPastDateError(false);
  };

  const getPopoverBodyType = (): IPopoverContentType | void => {
    if (!isClinicPhoneNumbersLoading && !clinicPhoneNumbers?.length) {
      return IPopoverContentType.Text_Messaging_unavailable;
    } else if (isDeliveryOptionDisabled) {
      return IPopoverContentType.Delivery_Option_Unavailable;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      size={confirming ? 'md' : 'xl'}
      autoFocus={false}
      isCentered={false}
    >
      <ModalHeader pb={1} size="lg">
        {confirming
          ? 'Are You Sure?'
          : servicesCount < 2
          ? 'Edit Service Reminder'
          : `Edit (${servicesCount}) Service Reminders`}
      </ModalHeader>
      <ModalCloseButton />

      <ModalBody>
        {!confirming ? (
          <>
            {servicesCount > 1 && (
              <Alert
                status="warning"
                mt={4}
                mb={6}
                title="Multiple reminders selected"
                description="Making edits will overwrite existing settings on these reminders."
                hideCloseButton
              />
            )}
            {serviceData && (
              <>
                <Flex align="center" mb={1} mt={4}>
                  <Text fontWeight="bold">Service Details</Text>
                  {integrationDataProvider && (
                    <InfoIcon
                      heading="Service Details"
                      text={`This is the ${
                        showBillingCode ? 'billing code and' : ''
                      } description of the service provided by ${integrationDataProvider}.`}
                    />
                  )}
                </Flex>
                <Box
                  borderColor="border.default"
                  borderWidth="1px"
                  borderRadius="lg"
                  p={3}
                  backgroundColor="background.subtle"
                  mb={6}
                >
                  {showBillingCode && (
                    <Text as="div">
                      <Text fontWeight="bold">Billing Code: </Text>
                      {serviceData.code}
                    </Text>
                  )}
                  <Text as="div">
                    <Text fontWeight="bold">Description: </Text>
                    {serviceData.pimsName}
                  </Text>
                </Box>

                <VStack align="flex-start" mb={6}>
                  <Flex align="center">
                    <Text fontWeight="bold">Display Name</Text>
                    <InfoIcon
                      heading="Display Name"
                      text="This is the service name clients will see in the reminder."
                    />
                  </Flex>
                  <TextInput value={displayName} onChange={handleDisplayNameChange} />
                </VStack>
              </>
            )}

            <Flex align="center" pb={3}>
              <Text fontWeight="bold" mr={2}>
                Active
              </Text>
              <Box>
                <Switch isChecked={active} isIndeterminate={indetermindateState} onChange={handleActiveChange} />
              </Box>
            </Flex>

            <Collapse in={!active && !indetermindateState} animateOpacity>
              <ScheduleServiceReminderActivation
                date={reminderActivationDate}
                onDateChange={handleDateChange}
                onDateRemove={handleRemoveDate}
              />
              {showPastDateError && (
                <Text as="div" mt={2} variant="danger">
                  Please pick a date and time in the future.
                </Text>
              )}
            </Collapse>

            <VStack align="flex-start" my={6}>
              <Flex align="center">
                <Text fontWeight="bold">Reminders</Text>
                <InfoIcon
                  heading="Reminders"
                  text="This is where you set the number and timing of reminders for this service. Reminder timing is based on service due date."
                />
              </Flex>
              {!!timings.length && (
                <VStack alignSelf="stretch" alignItems="flex-start">
                  {timings.map((timing, index) => (
                    <ReminderTimingRow
                      key={index}
                      index={index}
                      handleChange={handleTimingEdit}
                      handleDelete={handleRemoveTiming}
                      {...timing}
                    />
                  ))}
                </VStack>
              )}
              <Button variant="ghost" size="md" iconName="plus" onClick={handleAddTimingClick}>
                Add Reminder
              </Button>
              {showNoRemindersError && <Text variant="danger">Please add a reminder.</Text>}
              {showMatchingRemindersError && <Text variant="danger">Duplicate reminders are not allowed.</Text>}
              {showFalsyOffsetError && <Text variant="danger">Offset value must be greater than 0.</Text>}
              <Box>
                <Heading size="sm" mb={1} mt={6}>
                  Delivery Options
                </Heading>
                {showPostcards && (
                  <Text variant="subtle" size="sm">
                    A postcard will only send once on the day the service is due and is not affected by the reminders
                    schedule.
                  </Text>
                )}
                <List mt={3} spacing={1}>
                  <ListItem>
                    <InfoPopover
                      isPopoverDisabled={isDeliveryOptionDisabled}
                      Trigger={
                        <Checkbox
                          isChecked={isEmail}
                          isIndeterminate={isEmail === undefined}
                          onChange={(): void => setIsEmail((val) => !val)}
                          isInvalid={showMissingDeliveryOptionErr}
                          isDisabled={isDeliveryOptionDisabled}
                          mr={1}
                        >
                          Email
                        </Checkbox>
                      }
                      popoverText="You must add a Reminder before Email can be selected"
                    />
                  </ListItem>
                  <ListItem width="fit-content">
                    <TextMessagePopover popoverType={getPopoverBodyType()}>
                      <Checkbox
                        isChecked={isSms}
                        isIndeterminate={servicesCount > 1 && isSms === undefined}
                        onChange={(): void => setIsSms((val) => !val)}
                        isInvalid={showMissingDeliveryOptionErr}
                        mr={1}
                        isDisabled={!clinicPhoneNumbers?.length || isDeliveryOptionDisabled}
                      >
                        <Text
                          size="md"
                          variant={
                            !isClinicPhoneNumbersLoading && !clinicPhoneNumbers?.length ? 'placeholder' : 'default'
                          }
                          cursor={
                            !isClinicPhoneNumbersLoading && !clinicPhoneNumbers?.length ? 'not-allowed' : 'pointer'
                          }
                        >
                          Text Message
                        </Text>
                      </Checkbox>
                    </TextMessagePopover>
                  </ListItem>
                  <HasFeature name={FeatureFlagName.ServiceRemindersPushNotifications}>
                    <ListItem>
                      <InfoPopover
                        isPopoverDisabled={isDeliveryOptionDisabled}
                        Trigger={
                          <Checkbox
                            isChecked={isPush}
                            isIndeterminate={isPush === undefined}
                            onChange={(): void => setIsPush((val) => !val)}
                            isInvalid={showMissingDeliveryOptionErr}
                            isDisabled={isDeliveryOptionDisabled}
                            mr={1}
                          >
                            App Notification
                          </Checkbox>
                        }
                        popoverText="You must add a Reminder before App Notification can be selected"
                      />
                    </ListItem>
                  </HasFeature>
                  <ListItem>
                    <Checkbox
                      isChecked={isPetPortal}
                      isIndeterminate={isPetPortal === undefined}
                      onChange={(): void => setIsPetPortal((val) => !val)}
                      isInvalid={showMissingDeliveryOptionErr}
                      mr={1}
                    >
                      Pet Portal
                    </Checkbox>
                  </ListItem>
                  {showPostcards && (
                    <ListItem>
                      <Checkbox
                        isChecked={isPostcard}
                        isIndeterminate={servicesCount > 1 && isPostcard === undefined}
                        onChange={(): void => setIsPostcard((val) => !val)}
                        isInvalid={showMissingDeliveryOptionErr}
                        mr={1}
                      >
                        Postcard
                      </Checkbox>
                    </ListItem>
                  )}
                </List>
                {showMissingDeliveryOptionErr && (
                  <Text variant="danger" mt={3}>
                    Please select a delivery option.
                  </Text>
                )}
              </Box>
            </VStack>
          </>
        ) : (
          <Text>
            You&apos;re about to make changes to {servicesCount} service reminders. Are you sure you want to save these
            changes?
          </Text>
        )}
      </ModalBody>
      <ModalFooterButtons
        isLoading={loading}
        onSubmit={handleSave}
        submitText={confirming ? 'Confirm' : servicesCount === 1 ? 'Save' : `Save (${servicesCount})`}
      />
    </Modal>
  );
};

export default EditRemindersModal;
